import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet as Head } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { MarketingHeader } from "@/components/MarketingHeader";
import { MarketingFooter } from "@/components/MarketingFooter";
import { SchemaScript, generateBreadcrumbSchema } from "@/lib/seoHelpers";
import { getAllPublishableNvrs } from "@/lib/nvrSlug";
import { getCategoryCounts, getEquipmentByBrand, categoryInfo, MOUNT_KIND_LABEL } from "@/lib/equipmentSlug";

const SITE = "https://cctvplanner.io";

/**
 * Equipment hubs, there to be crawled like `/nvr`: `/equipment` lists every
 * category with its count, `/equipment/<category>` every model in it grouped by
 * maker. Copy is counted from the catalog; nothing is said the rows don't say.
 */
export default function EquipmentIndex() {
  const { category } = useParams<{ category?: string }>();
  const { t } = useTranslation();
  const counts = useMemo(() => getCategoryCounts(), []);
  const info = category ? categoryInfo(category) : undefined;
  const groups = useMemo(() => (category ? getEquipmentByBrand(category) : []), [category]);
  const nvrCount = useMemo(() => getAllPublishableNvrs().length, []);

  if (category && (!info || groups.length === 0)) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <Head>
          <title>{t("equipment_not_found_title", { defaultValue: "Not found — CCTVplanner" })}</title>
          <meta name="robots" content="noindex,follow" />
          <meta name="prerender-status-code" content="404" />
        </Head>
        <Link to="/equipment" className="text-[var(--color-brand-blue-text)] hover:underline">
          {t("equipment_back_to_catalog", { defaultValue: "Back to the equipment catalog" })}
        </Link>
      </div>
    );
  }

  const total = category ? groups.reduce((n, g) => n + g.items.length, 0) : counts.reduce((n, c) => n + c.count, 0) + nvrCount;
  const heading = info ? info.many : t("equipment_index_heading", { defaultValue: "Security equipment catalog" });
  const title = info
    ? `${info.many} — ${total} Models with Datasheet Specs | CCTVplanner`
    : t("equipment_index_title", { defaultValue: "Security Equipment Catalog — {{count}} Models with Datasheet Specs | CCTVplanner", count: total });
  const description = info
    ? `${total} ${info.many.toLowerCase()} from ${groups.length} manufacturers, each with the specifications its maker publishes. Plan a CCTV or security system with any of them for free.`
    : t("equipment_index_description", {
        defaultValue: "{{count}} recorders, PoE switches, UPS, power supplies, mounts, alarm and access-control devices, each with the specifications its maker publishes. Plan a system with any of them for free.",
        count: total,
      });
  const canonical = info ? `${SITE}/equipment/${info.id}` : `${SITE}/equipment`;

  return (
    <div className="min-h-screen bg-[var(--color-bg)]">
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
      </Head>
      <SchemaScript schema={generateBreadcrumbSchema([
        { name: t("breadcrumb_home", { defaultValue: "Home" }), url: "/" },
        { name: t("equipment_breadcrumb", { defaultValue: "Equipment" }), url: "/equipment" },
        ...(info ? [{ name: info.many, url: `/equipment/${info.id}` }] : []),
      ])} />

      <MarketingHeader />

      <main id="main-content">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <nav className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-foreground transition-colors">{t("breadcrumb_home", { defaultValue: "Home" })}</Link>
            <ChevronRight className="w-3 h-3" aria-hidden="true" />
            {info ? (
              <>
                <Link to="/equipment" className="hover:text-foreground transition-colors">{t("equipment_breadcrumb", { defaultValue: "Equipment" })}</Link>
                <ChevronRight className="w-3 h-3" aria-hidden="true" />
                <span className="text-foreground">{info.many}</span>
              </>
            ) : (
              <span className="text-foreground">{t("equipment_breadcrumb", { defaultValue: "Equipment" })}</span>
            )}
          </nav>

          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-3">{heading}</h1>
          <p className="text-[var(--color-text-muted)] mb-8">
            {info
              ? t("equipment_category_count", { defaultValue: "{{count}} models from {{brands}} manufacturers.", count: total, brands: groups.length })
              : t("equipment_index_count", { defaultValue: "{{count}} models with their makers' specifications.", count: total })}
          </p>

          {!info && (
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 mb-10">
              <li>
                <Link to="/nvr" className="block py-1.5 hover:text-[var(--color-brand-blue-text)] transition-colors">
                  {t("nvr_index_breadcrumb", { defaultValue: "Recorders" })} <span className="opacity-60">({nvrCount})</span>
                </Link>
              </li>
              {counts.map(c => (
                <li key={c.id}>
                  <Link to={`/equipment/${c.id}`} className="block py-1.5 hover:text-[var(--color-brand-blue-text)] transition-colors">
                    {c.many} <span className="opacity-60">({c.count})</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}

          {groups.map(group => (
            <section key={group.manufacturer} className="mb-10">
              <h2 className="text-lg font-semibold mb-1">{group.manufacturer}</h2>
              <p className="text-sm text-[var(--color-text-muted)] mb-3">
                {t("nvr_index_brand_count", { defaultValue: "{{count}} models", count: group.items.length })}
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
                {group.items.map(e => (
                  <li key={e.path}>
                    <Link to={e.path} className="block py-1.5 text-sm hover:text-[var(--color-brand-blue-text)] transition-colors">
                      {e.model}
                      {e.mountKind && <span className="opacity-60"> ({MOUNT_KIND_LABEL[e.mountKind].toLowerCase()})</span>}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </main>

      <MarketingFooter />
    </div>
  );
}
